import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoChevronDown, IoChevronUp } from 'react-icons/io5';
import { BsCheckCircleFill } from 'react-icons/bs';
import { HiChevronDoubleRight, HiChevronDoubleLeft } from 'react-icons/hi';
import { HiOutlineTicket } from 'react-icons/hi';
import { BsCoin } from 'react-icons/bs';
import { MdCardGiftcard } from 'react-icons/md';

import { getCourseStatus } from 'utils';

import { DetailContentInterface, ProgressInterface, RewardsConfig } from 'types';

import { AiFillFileText, AiFillVideoCamera } from 'react-icons/ai';
import { FaFileDownload } from 'react-icons/fa';
import { MdQuiz } from 'react-icons/md';
import { IoGameController } from 'react-icons/io5';
import { HiMiniSpeakerWave } from 'react-icons/hi2';

import './style.scss';

export type NextLessonsProps = {
  nextLessons: DetailContentInterface[];
  courseID: string;
  folderID: string;
  allProgress?: ProgressInterface[];
  rewardsConfig?: RewardsConfig | null;
  breadcrumbs?: any[];
  className?: string;
  onCollapsedChange?: (collapsed: boolean) => void;
  [key: string]: any;
};

type FolderNodeProps = {
  folder: ProgressInterface;
  courseID: string;
  currentContentID: string;
  currentFolderID: string;
  nextLessons: DetailContentInterface[];
  expandedMap: Record<string, boolean>;
  onToggle: (id: string) => void;
  onNavigate: (folderID: string, contentID: string) => void;
  entityIcon: (item: any) => JSX.Element;
  getTypeLabel: (type: string) => string;
  depth?: number;
};

function FolderNode({
  folder,
  courseID,
  currentContentID,
  currentFolderID,
  nextLessons,
  expandedMap,
  onToggle,
  onNavigate,
  entityIcon,
  getTypeLabel,
  depth = 0,
}: FolderNodeProps) {
  const isExpanded = !!expandedMap[folder._id];
  const isCurrentFolder = folder._id === currentFolderID;

  const subFolders = (folder.items ?? []).filter(i => i.folder);
  const contents = (folder.items ?? []).filter(i => !i.folder);

  // Se é a pasta atual (leaf folder), usa nextLessons para ter active/percent corretos
  const displayContents: any[] = isCurrentFolder ? nextLessons : contents;

  return (
    <div className={depth > 0 ? 'pl-3' : ''}>
      <button
        onClick={() => onToggle(folder._id)}
        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-colors ${
          isCurrentFolder ? 'bg-orange/5' : 'hover:bg-gray-50'
        }`}
      >
        <span className={`text-sm font-semibold text-left ${isCurrentFolder ? 'text-[#181b1a]' : 'text-gray-600'}`}>
          {folder.title}
        </span>
        {isExpanded ? (
          <IoChevronUp className="text-gray-400 text-sm flex-shrink-0 ml-2" />
        ) : (
          <IoChevronDown className="text-gray-400 text-sm flex-shrink-0 ml-2" />
        )}
      </button>

      {isExpanded && (
        <div className="pb-1">
          {subFolders.map(sub => (
            <FolderNode
              key={sub._id}
              folder={sub}
              courseID={courseID}
              currentContentID={currentContentID}
              currentFolderID={currentFolderID}
              nextLessons={nextLessons}
              expandedMap={expandedMap}
              onToggle={onToggle}
              onNavigate={onNavigate}
              entityIcon={entityIcon}
              getTypeLabel={getTypeLabel}
              depth={depth + 1}
            />
          ))}

          {/* Conteúdos diretos */}
          {displayContents.map((lesson: any) => {
            if (lesson.folder) return null;

            const isActive = lesson.active || lesson._id === currentContentID;
            const isCompleted = lesson.percent === 100;
            const isLocked = lesson.open === false;

            return (
              <button
                key={lesson._id}
                type="button"
                onClick={() => {
                  if (isActive || isLocked) return;
                  onNavigate(isCurrentFolder ? currentFolderID : folder._id, lesson._id);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all text-left border-l-4
                  ${isActive ? 'bg-orange/10 border-orange' : 'border-transparent hover:bg-gray-50'}
                  ${isLocked ? 'opacity-50 cursor-not-allowed' : isActive ? 'cursor-default' : 'cursor-pointer'}`}
              >
                <div className="flex-shrink-0">
                  {isCompleted ? (
                    <BsCheckCircleFill className="text-[#2bb788] text-lg" />
                  ) : isActive ? (
                    <div className="w-4 h-4 rounded-full bg-orange flex items-center justify-center">
                      <div className="w-1.5 h-1.5 rounded-full bg-white" />
                    </div>
                  ) : (
                    <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <span
                    className={`block text-sm font-medium leading-tight truncate ${
                      isActive ? 'text-orange' : 'text-[#181b1a]'
                    }`}
                  >
                    {lesson.title}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
                    <span className="inline-flex text-[10px]">{entityIcon(lesson)}</span>
                    {getTypeLabel(lesson.type)}
                    {lesson.data?.duration ? ` • ${lesson.data.duration}` : ''}
                  </span>
                  {isActive && (
                    <span className="text-[10px] text-orange font-medium">Reproduzindo...</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function NextLessons({
  nextLessons,
  courseID,
  folderID,
  allProgress,
  rewardsConfig,
  breadcrumbs,
  className,
  onCollapsedChange,
  ...rest
}: NextLessonsProps) {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({});
  const [lastFolderID, setLastFolderID] = useState<string>('');

  const getPercent = useMemo(() => {
    if (!allProgress?.length) return 0;
    return allProgress.find(item => item._id === courseID)?.percent ?? 0;
  }, [allProgress, courseID]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _status = useMemo(() => getCourseStatus(getPercent), [getPercent]);

  const getCheckpointIcon = useMemo(() => {
    const ticketsEnabled = rewardsConfig?.tickets?.enabled ?? false;
    const coinsEnabled = rewardsConfig?.coins?.enabled ?? false;

    if (ticketsEnabled) {
      return 'ticket'; // Bilhetes têm prioridade
    } else if (coinsEnabled) {
      return 'coin';
    } else {
      return 'gift';
    }
  }, [rewardsConfig]);

  const getCheckpointIconComponent = (type: string) => {
    switch (type) {
      case 'ticket':
        return <HiOutlineTicket className={`text-xs`} />;
      case 'coin':
        return <BsCoin className={`text-xs`} />;
      case 'gift':
        return <MdCardGiftcard className={`text-xs`} />;
      default:
        return <HiOutlineTicket className={`text-xs`} />;
    }
  };

  const getCheckpointTitle = (checkpoint: number) => {
    const ticketsEnabled = rewardsConfig?.tickets?.enabled ?? false;
    const coinsEnabled = rewardsConfig?.coins?.enabled ?? false;

    if (ticketsEnabled) {
      return checkpoint === 70 ? '70% — Bilhete Prata + Badge' : '100% — Bilhete Ouro + Badge';
    } else if (coinsEnabled) {
      return checkpoint === 70 ? '70% — Moedas + Badge' : '100% — Moedas + Badge';
    } else {
      return checkpoint === 70 ? '70% — Presente + Badge' : '100% — Presente + Badge';
    }
  };

  const handleCollapse = (value: boolean) => {
    setCollapsed(value);
    onCollapsedChange?.(value);
  };

  const entityIcon = (item: any): JSX.Element => {
    if (item.type === 'video') return <AiFillVideoCamera />;
    if (item.type === 'text') return <AiFillFileText />;
    if (item.type === 'doc') return <FaFileDownload />;
    if (item.type === 'quiz') return <MdQuiz />;
    if (item.type === 'audio') return <HiMiniSpeakerWave />;
    if (!!item.type?.match(/game_/gi)) return <IoGameController />;
    return <></>;
  };

  const getTypeLabel = (type: string) => {
    if (type === 'video') return 'Vídeo';
    if (type === 'text') return 'Texto';
    if (type === 'doc') return 'Documento';
    if (type === 'quiz') return 'Avaliação';
    if (type === 'audio') return 'Áudio';
    if (type?.match(/game_/gi)) return 'Minigame';
    return '';
  };

  const topFolders = useMemo((): ProgressInterface[] => {
    if (!allProgress?.length) return [];
    const courseProgress = allProgress.find(p => p._id === courseID);
    if (!courseProgress?.items?.length) return [];
    return courseProgress.items.filter(item => item.folder);
  }, [allProgress, courseID]);

  const findAncestors = useCallback(
    (items: ProgressInterface[], targetID: string, path: string[]): string[] | null => {
      for (const item of items) {
        if (!item.folder) continue;
        const newPath = [...path, item._id];
        if (item._id === targetID) return newPath;
        if (item.items?.length) {
          const found = findAncestors(item.items, targetID, newPath);
          if (found) return found;
        }
      }
      return null;
    },
    []
  );

  if (lastFolderID !== folderID) {
    const ancestors = findAncestors(topFolders, folderID, []);
    const map: Record<string, boolean> = {};
    if (ancestors) {
      ancestors.forEach(id => { map[id] = true; });
    } else if (topFolders.length) {
      map[topFolders[0]._id] = true;
    }
    setExpandedMap(map);
    setLastFolderID(folderID);
  }

  const toggleFolder = useCallback((id: string) => {
    setExpandedMap(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const handleNavigate = useCallback(
    (targetFolderID: string, contentID: string) => {
      navigate(`/cursos/${courseID}/${targetFolderID}/${contentID}`);
    },
    [navigate, courseID]
  );

  // Conteúdo ativo atual (para marcar o item como ativo)
  const currentContentID = useMemo(
    () => nextLessons.find(l => l.active)?._id ?? '',
    [nextLessons]
  );

  if (collapsed) {
    return (
      <div className="flex-shrink-0 flex flex-col items-center">
        <button
          onClick={() => handleCollapse(false)}
          className="p-2 mt-7 text-gray-400 hover:text-orange transition-colors"
          title="Expandir conteúdo"
        >
          <HiChevronDoubleLeft className="text-xl" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-stretch" {...rest}>
      <button
        onClick={() => handleCollapse(true)}
        className="flex-shrink-0 hidden lg:flex items-center justify-center w-6 self-start mt-7 bg-white border border-gray-100 border-r-0 rounded-l-lg py-3 text-gray-400 hover:text-orange transition-colors shadow-sm"
        title="Recolher conteúdo"
      >
        <HiChevronDoubleRight className="text-sm" />
      </button>

      <div className={`flex-shrink-0 w-full lg:w-[320px] xl:w-[350px] bg-white border-l border-gray-100 ${className || ''}`}>
        <div className="flex items-center px-5 pt-7 pb-5">
          <h4 className="text-lg font-semibold text-[#181b1a]">Conteúdo do Curso</h4>
        </div>

        <div className="px-5 pb-8">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-gray-400 font-medium">Progresso do curso</span>
            <span className="text-xs text-gray-500 font-semibold">{Math.round(getPercent)}%</span>
          </div>
          <div className="relative w-full">
            <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full bg-[#2bb788] transition-all duration-500"
                style={{ width: `${Math.round(getPercent)}%` }}
              />
            </div>
            <div
              className="absolute top-1/2 -translate-y-1/2"
              style={{ left: '70%', transform: 'translate(-50%, -50%)' }}
            >
              <div
                className={`w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all ${
                  getPercent >= 70
                    ? 'bg-gray-200 border-gray-400'
                    : 'bg-white border-gray-200'
                }`}
                title={getCheckpointTitle(70)}
              >
                <div className={`text-xs ${getPercent >= 70 ? 'text-gray-600' : 'text-gray-300'}`}>
                  {getCheckpointIconComponent(getCheckpointIcon)}
                </div>
              </div>
            </div>
            <div
              className="absolute top-1/2"
              style={{ left: '100%', transform: 'translate(-50%, -50%)' }}
            >
              <div
                className={`w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all ${
                  getPercent >= 100
                    ? 'bg-amber-100 border-amber-400'
                    : 'bg-white border-gray-200'
                }`}
                title={getCheckpointTitle(100)}
              >
                <div className={`text-xs ${getPercent >= 100 ? 'text-amber-500' : 'text-gray-300'}`}>
                  {getCheckpointIconComponent(getCheckpointIcon)}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[calc(100vh-280px)] px-2">
          {topFolders.length > 0 ? (
            topFolders.map(trail => (
              <FolderNode
                key={trail._id}
                folder={trail}
                courseID={courseID}
                currentContentID={currentContentID}
                currentFolderID={folderID}
                nextLessons={nextLessons}
                expandedMap={expandedMap}
                onToggle={toggleFolder}
                onNavigate={handleNavigate}
                entityIcon={entityIcon}
                getTypeLabel={getTypeLabel}
                depth={0}
              />
            ))
          ) : (
            // Fallback: curso sem estrutura de trilhas
            <div className="pb-2">
              {nextLessons.map((lesson: any) => {
                if (lesson.folder) return null;
                const isActive = lesson.active;
                const isCompleted = lesson.percent === 100;
                const isLocked = lesson.open === false;
                return (
                  <button
                    key={lesson._id}
                    type="button"
                    onClick={() => {
                      if (isActive || isLocked) return;
                      handleNavigate(folderID, lesson._id);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all text-left border-l-4
                      ${isActive ? 'bg-orange/10 border-orange' : 'border-transparent hover:bg-gray-50'}
                      ${isLocked ? 'opacity-50 cursor-not-allowed' : isActive ? 'cursor-default' : 'cursor-pointer'}`}
                  >
                    <div className="flex-shrink-0">
                      {isCompleted ? (
                        <BsCheckCircleFill className="text-[#2bb788] text-lg" />
                      ) : isActive ? (
                        <div className="w-4 h-4 rounded-full bg-orange flex items-center justify-center">
                          <div className="w-1.5 h-1.5 rounded-full bg-white" />
                        </div>
                      ) : (
                        <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`block text-sm font-medium leading-tight truncate ${isActive ? 'text-orange' : 'text-[#181b1a]'}`}>
                        {lesson.title}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
                        <span className="inline-flex text-[10px]">{entityIcon(lesson)}</span>
                        {getTypeLabel(lesson.type)}
                      </span>
                      {isActive && <span className="text-[10px] text-orange font-medium">Reproduzindo...</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
